import * as React from 'react';
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { observer } from 'mobx-react-lite';
import { getTimeSuggestionsStore } from '../selectors/getTimeSuggestionsStore';
import loc from 'owa-localize';
import { timeSuggestionSettings, showtimeSuggestions } from './TimeSuggestions.locstring.json';
import { logUsage } from 'owa-analytics';
import {
    onDurationChanged,
    onTimingChanged,
    hideSearchCriteriaDialog,
} from '../mutators/searchCriteriaDialogMutators';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { settingsDurationLabel, settingsTimingLabel } from './SearchCriteriaDialog.locstring.json';
import createDurationDropdownOptions from '../utils/durationOptionsUtils';
import { timingDropdownOptions } from '../utils/createTimingDropdownOptions';
import { updateTimeSuggestionsWithCriteria } from '../actions/dialogActions';
import getIsTimePanelView from '../selectors/getIsTimePanelView';

import styles from './SearchCriteriaDialog.scss';

export interface SearchCriteriaDialogProps {
    calendarEvent: CalendarEvent;
    containerRectLeft: number;
    viewStateId: string | null;
}

let durationDropdownOptions: IDropdownOption[];

export default observer(function SearchCriteriaDialog(props: SearchCriteriaDialogProps) {
    const { calendarEvent, containerRectLeft, viewStateId } = props;
    const {
        showSearchCriteriaDialog: showDialog,
        durationSearchCriteria: currentDuration,
        timingSearchCriteria: currentTiming,
    } = getTimeSuggestionsStore(viewStateId);

    durationDropdownOptions = createDurationDropdownOptions(viewStateId);
    const durationDropdownLabel = loc(settingsDurationLabel);
    const timingDropdownLabel = loc(settingsTimingLabel);
    const isTimePanelView = getIsTimePanelView(viewStateId);
    let dialogStyles;
    if (isTimePanelView) {
        dialogStyles = {
            main: {
                marginRight: '15px',
                marginLeft: containerRectLeft + 'px',
            },
        };
    }

    const onUpdateTimeSuggestions = () => {
        updateTimeSuggestionsWithCriteria(calendarEvent, viewStateId);
        logUsage('RefineTimeSuggestionSpecification', {
            urgency: currentTiming,
            duration: currentDuration,
        });
        hideDialog();
    };

    const hideDialog = () => {
        hideSearchCriteriaDialog(viewStateId);
    };

    const onDurationChange = React.useCallback(
        (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
            onDurationChanged(option, viewStateId);
        },
        [viewStateId]
    );

    const onTimingChange = React.useCallback(
        (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
            onTimingChanged(option, viewStateId);
        },
        [viewStateId]
    );

    return (
        <Dialog
            containerClassName={styles.dialogContainer}
            hidden={!showDialog}
            modalProps={{
                isBlocking: false,
            }}
            onDismiss={hideDialog}
            dialogContentProps={{
                type: DialogType.close,
                title: loc(timeSuggestionSettings),
            }}
            styles={isTimePanelView ? dialogStyles : null}>
            <div className={styles.durationContainer}>
                <span className={styles.durationLabel}>{durationDropdownLabel}</span>
                <Dropdown
                    className={styles.durationDropdown}
                    onChange={onDurationChange}
                    options={durationDropdownOptions}
                    selectedKey={currentDuration}
                    ariaLabel={durationDropdownLabel}
                />
            </div>
            <div className={styles.timingContainer}>
                <span className={styles.timingLabel}>{timingDropdownLabel}</span>
                <Dropdown
                    className={styles.timingDropdown}
                    onChange={onTimingChange}
                    options={timingDropdownOptions}
                    selectedKey={currentTiming}
                    ariaLabel={timingDropdownLabel}
                />
            </div>
            <DialogFooter>
                <PrimaryButton onClick={onUpdateTimeSuggestions} text={loc(showtimeSuggestions)} />
                <DefaultButton onClick={hideDialog} text={loc(cancelButton)} />
            </DialogFooter>
        </Dialog>
    );
});
